import { useEffect, useRef } from 'react';
import { scrollToTopOf } from '../lib/scrollTop';
import { PendingButton } from '../components/common/PendingButton';
import type { PortalRequestReply } from '@bbbk/schema';
import { Avatar } from '../components/common';
import { G } from './glyphs';
import { BookBrand } from './RequestChrome';
import { dogNames, money, sentLines } from './requestRules';

// `c28` — Booking requested (flow 2.1, R-REQ-6, AC12).
//
// A confirmation, not the review again. The clock says waiting — the page does
// not celebrate a yes that has not happened. One compact card says what went,
// from what the server WROTE (the reply), not from what the form held; and
// the page's real content is *What happens next*: the customer's step done,
// Patricia's live, the deposit and the yes still to come.

const initialOf = (name: string) => (name.trim()[0] ?? '?').toUpperCase();

export function RequestSent({ reply, onDone }: { reply: PortalRequestReply; onDone: () => void }) {
  // The receipt replaces the review step inside the same scroll (the drawer's
  // panel): it opens at the top, not where the review was left (#245 review).
  const page = useRef<HTMLElement>(null);
  useEffect(() => { scrollToTopOf(page.current); }, []);
  const lines = sentLines(reply);
  const breeds = reply.dogs.map((d) => d.breed).filter((b): b is string => !!b);
  return (
    <main className="cp-app cp-book" ref={page}>
      <BookBrand />
      <div className="cp-check cs-done">
        <span className="cp-tick wait" aria-hidden="true">
          {G.waiting({ size: 30 })}
        </span>
        <h1>Booking requested</h1>
        <p className="cp-p mute cs-done-sub">Sent to Patricia — she’ll be in touch shortly.</p>

        <p className="cp-eyebrow cs-eyebrow">Your request</p>
        <div className="cp-card cs-sum">
          <div className="cs-who">
            <span className="cs-avs">
              {reply.dogs.map((d) => (
                <Avatar key={d.id} name={d.name} id={d.id} size={36} kind="dog" initials={initialOf(d.name)} />
              ))}
            </span>
            <div>
              <b>{dogNames(reply.dogs.map((d) => d.name))}</b>
              {breeds.length > 0 && <small>{breeds.join(' · ')}</small>}
            </div>
          </div>
          <div className="cs-sum-l">
            {G.calendar({ size: 14 })}
            <span>{lines[0]}</span>
          </div>
          <div className="cs-sum-l">
            {G.sun({ size: 14 })}
            <span>{lines[1]}</span>
          </div>
          <div className="cs-sum-l">
            {G.euro({ size: 14 })}
            <span>{lines[2]}</span>
          </div>
        </div>

        <p className="cp-eyebrow cs-eyebrow">What happens next</p>
        <div className="cp-card cs-next">
          <div className="cp-timeline">
            <div className="t done s-req">
              <span className="d">{G.clipboardCheck({ size: 14 })}</span>
              <span>
                Requested
                <br />
                <small>Just now</small>
              </span>
            </div>
            <div className="t now s-acc">
              <span className="d">{G.thumbsUp({ size: 14 })}</span>
              <span>
                Accepted
                <br />
                <small>Patricia usually the same day. You will see it here.</small>
              </span>
            </div>
            <div className="t todo s-dep">
              <span className="d">{G.euro({ size: 14 })}</span>
              <span>
                Deposit paid
                <br />
                <small>{reply.inFull ? `${money(reply.total)} in full to secure booking` : `${money(reply.deposit)} required to secure booking`}</small>
              </span>
            </div>
            <div className="t todo s-con">
              <span className="d">{G.calendarCheck({ size: 14 })}</span>
              <span>
                Booked
                <br />
                <small>The dates are yours</small>
              </span>
            </div>
          </div>
        </div>

        <div className="cp-actions">
          {/* Home is a fresh page load (it must re-read the stays): busy until it replaces this. */}
          <PendingButton className="cp-btn primary" icon={G.home({ size: 15 })} leaves onClick={onDone}>
            Go to stays
          </PendingButton>
        </div>
      </div>
    </main>
  );
}
